import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { studentAPI } from '@/services/api';
import toast from 'react-hot-toast';

const TextField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = 'text',
  error,
  required = false,
  disabled = false,
  readonly = false,
  note = null,
}) => {
  const sharedClass = `w-full rounded-xl border-2 bg-white px-4 py-3.5 text-sm text-slate-800 outline-none transition-all ${
    disabled || readonly
      ? 'bg-slate-50 border-slate-200 cursor-not-allowed'
      : error
      ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
      : 'border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10'
  }`;

  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        className={sharedClass}
        value={value}
        onChange={(event) => onChange(name, event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
      />
      {note && <p className="mt-1 text-xs text-slate-500">{note}</p>}
      {error ? <p className="mt-2 text-xs font-medium text-red-500">{error}</p> : null}
    </div>
  );
};

const SelectField = ({ label, name, value, onChange, options, error, required = false, disabled = false }) => (
  <div>
    <label className="mb-2 block text-sm font-semibold text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      value={value}
      onChange={(event) => onChange(name, event.target.value)}
      disabled={disabled}
      className={`w-full rounded-xl border-2 bg-white px-4 py-3.5 text-sm text-slate-800 outline-none transition-all ${
        disabled
          ? 'bg-slate-50 border-slate-200 cursor-not-allowed'
          : error
          ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
          : 'border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10'
      }`}
    >
      <option value="">Select {label}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    {error ? <p className="mt-2 text-xs font-medium text-red-500">{error}</p> : null}
  </div>
);

const SectionTitle = ({ icon, title, subtitle }) => (
  <div className="mb-8">
    <div className="flex items-center gap-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-primary to-primary-600 text-white shadow-islamic">
        <i className={`fas ${icon}`} />
      </div>
      <div>
        <h2 className="text-2xl font-black text-slate-900">{title}</h2>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
    </div>
  </div>
);

const StudentEditProfile = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const GENDER_OPTIONS = ['Male', 'Female'];
  const RELATIONSHIP_OPTIONS = ['Father', 'Mother', 'Guardian', 'Uncle', 'Aunt', 'Other'];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await studentAPI.getProfile();
        const studentData = res.data.data;
        setStudent(studentData);
        setFormData({
          first_name: studentData.first_name || '',
          last_name: studentData.last_name || '',
          middle_name: studentData.middle_name || '',
          phone: studentData.phone || '',
          date_of_birth: studentData.date_of_birth ? studentData.date_of_birth.split('T')[0] : '',
          gender: studentData.gender || '',
          religion: studentData.religion || '',
          state_of_origin: studentData.state_of_origin || '',
          lga: studentData.lga || '',
          home_address: studentData.home_address || '',
          previous_school: studentData.previous_school || '',
          parent_name: studentData.parent_name || '',
          parent_phone: studentData.parent_phone || '',
          parent_email: studentData.parent_email || '',
          relationship: studentData.relationship || '',
          notes: studentData.notes || '',
        });
        setPhotoPreview(studentData.picture || studentData.photo || studentData.profile_picture);
      } catch (err) {
        console.error('Profile fetch error:', err);
        toast.error('Failed to load profile');
        navigate('/student/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [navigate]);

  const updateField = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
    setErrors((previous) => ({
      ...previous,
      [field]: '',
    }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{10,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Enter a valid phone number';
    }
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    }
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }
    if (!formData.parent_name.trim()) {
      newErrors.parent_name = 'Parent/guardian name is required';
    }
    if (!formData.parent_phone.trim()) {
      newErrors.parent_phone = 'Parent phone number is required';
    } else if (!/^\+?[\d\s-]{10,15}$/.test(formData.parent_phone.trim())) {
      newErrors.parent_phone = 'Enter a valid parent phone number';
    }
    if (formData.parent_email.trim() && !/\S+@\S+\.\S+/.test(formData.parent_email.trim())) {
      newErrors.parent_email = 'Enter a valid parent email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = { ...formData };

      // Remove empty fields
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === '' || dataToSend[key] === null || dataToSend[key] === undefined) {
          delete dataToSend[key];
        }
      });

      // Add photo if selected
      if (photoFile) {
        // For file uploads, we need to use FormData
        const formDataToSend = new FormData();
        Object.keys(dataToSend).forEach(key => {
          formDataToSend.append(key, dataToSend[key]);
        });
        formDataToSend.append('picture', photoFile);

        await studentAPI.updateProfile(formDataToSend);
      } else {
        // For regular updates, use JSON
        await studentAPI.updateProfile(dataToSend);
      }

      toast.success('Profile updated successfully');
      navigate('/student/profile');
    } catch (err) {
      console.error('Profile update error:', err);
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/student/profile');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black text-slate-800">Edit Profile</h2>
        <button
          onClick={handleCancel}
          className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-all"
        >
          Cancel
        </button>
      </div>

      {/* Personal Information Section */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
        <SectionTitle
          icon="fa-user"
          title="Personal Information"
          subtitle="Update your personal details and contact information"
        />

        <div className="grid md:grid-cols-2 gap-6">
          <TextField
            label="First Name"
            name="first_name"
            value={formData.first_name}
            onChange={updateField}
            placeholder="Enter your first name"
            required
            error={errors.first_name}
          />
          <TextField
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            onChange={updateField}
            placeholder="Enter your last name"
            required
            error={errors.last_name}
          />
          <TextField
            label="Middle Name"
            name="middle_name"
            value={formData.middle_name}
            onChange={updateField}
            placeholder="Enter your middle name (optional)"
          />
          <TextField
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={updateField}
            placeholder="e.g. +2348012345678"
            required
            error={errors.phone}
          />
          <TextField
            label="Date of Birth"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={updateField}
            type="date"
            required
            error={errors.date_of_birth}
          />
          <SelectField
            label="Gender"
            name="gender"
            value={formData.gender}
            onChange={updateField}
            options={GENDER_OPTIONS}
            required
            error={errors.gender}
          />
          <TextField
            label="Religion"
            name="religion"
            value={formData.religion}
            onChange={updateField}
            placeholder="e.g. Islam, Christianity"
          />
          <TextField
            label="State of Origin"
            name="state_of_origin"
            value={formData.state_of_origin}
            onChange={updateField}
            placeholder="Enter your state of origin"
          />
          <TextField
            label="Local Government Area"
            name="lga"
            value={formData.lga}
            onChange={updateField}
            placeholder="Enter your LGA"
          />
          <div className="md:col-span-2">
            <TextField
              label="Home Address"
              name="home_address"
              value={formData.home_address}
              onChange={updateField}
              placeholder="Enter your complete home address"
            />
          </div>
          <TextField
            label="Previous School"
            name="previous_school"
            value={formData.previous_school}
            onChange={updateField}
            placeholder="Enter your previous school"
          />
          <div className="md:col-span-2">
            <TextField
              label="Extra Notes"
              name="notes"
              value={formData.notes}
              onChange={updateField}
              placeholder="Optional notes for the admission team"
              textarea
            />
          </div>
        </div>
      </div>

      {/* Parent/Guardian Section */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
        <SectionTitle
          icon="fa-users"
          title="Parent/Guardian Information"
          subtitle="Update your parent or guardian contact details"
        />

        <div className="grid md:grid-cols-2 gap-6">
          <TextField
            label="Parent/Guardian Full Name"
            name="parent_name"
            value={formData.parent_name}
            onChange={updateField}
            placeholder="Enter parent/guardian full name"
            required
            error={errors.parent_name}
          />
          <TextField
            label="Parent/Guardian Phone"
            name="parent_phone"
            value={formData.parent_phone}
            onChange={updateField}
            placeholder="e.g. +2348012345678"
            required
            error={errors.parent_phone}
          />
          <TextField
            label="Parent/Guardian Email"
            name="parent_email"
            value={formData.parent_email}
            onChange={updateField}
            placeholder="parent@example.com"
            type="email"
            error={errors.parent_email}
          />
          <SelectField
            label="Relationship"
            name="relationship"
            value={formData.relationship}
            onChange={updateField}
            options={RELATIONSHIP_OPTIONS}
          />
        </div>
      </div>

      {/* Profile Photo Section */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
        <SectionTitle
          icon="fa-camera"
          title="Profile Photo"
          subtitle="Upload a new passport photo (optional)"
        />

        <div className="flex items-center gap-6">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center overflow-hidden">
            {photoPreview ? (
              <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <i className="fas fa-user text-2xl text-slate-400" />
            )}
          </div>
          <div className="flex-1">
            <label className="block">
              <span className="sr-only">Choose profile photo</span>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
              />
            </label>
            <p className="mt-1 text-sm text-slate-500">PNG, JPG, GIF up to 5MB</p>
          </div>
        </div>
      </div>

      {/* Read-only Information */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
        <SectionTitle
          icon="fa-info-circle"
          title="Account Information"
          subtitle="These fields cannot be changed"
        />

        <div className="grid md:grid-cols-2 gap-6">
          <TextField
            label="Email Address"
            name="email"
            value={student?.email || ''}
            onChange={() => {}}
            readonly
            note="Email cannot be changed. Contact admin if needed."
          />
          <TextField
            label="Admission Number"
            name="admission_number"
            value={student?.admission_number || ''}
            onChange={() => {}}
            readonly
          />
          <TextField
            label="Current Class"
            name="class"
            value={student?.class || ''}
            onChange={() => {}}
            readonly
            note="Only admin can change class"
          />
          <TextField
            label="Account Status"
            name="status"
            value={student?.status || ''}
            onChange={() => {}}
            readonly
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default StudentEditProfile;